import { handleUpload } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { pipe } from 'lodash/fp'

const SHAPE_SELECTOR = 'path, circle, rect, ellipse, polygon, line, polyline'
const NAMED_BLACK = /^(black|currentColor)$/i
const HEX_OR_RGB = /#([0-9a-fA-F]{3}){1,2}\b|rgba?\(/

const isReplaceableColor = (value: string | null | undefined): boolean => {
  if (!value) return false
  return HEX_OR_RGB.test(value.trim())
}

const normalizeNamedBlack = (value: string | null): string | null => {
  if (!value) return null
  return NAMED_BLACK.test(value.trim()) ? '#000000' : value
}

/**
 * Ensures Noun Project (and similar) SVGs have replaceable hex colors for the
 * designer color picker. Solid icons get an explicit fill; stroke/line icons
 * (common for arrows) get an explicit stroke so recoloring does not no-op on
 * fill="none" + inherited/default black strokes.
 */
export const ensureBlackFill = (svgString: string): string => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(svgString, 'image/svg+xml')
  const shapes = doc.querySelectorAll(SHAPE_SELECTOR)

  shapes.forEach((el: Element) => {
    const fill = normalizeNamedBlack(el.getAttribute('fill'))
    const stroke = normalizeNamedBlack(el.getAttribute('stroke'))

    if (fill && fill !== el.getAttribute('fill')) {
      el.setAttribute('fill', fill)
    }
    if (stroke && stroke !== el.getAttribute('stroke')) {
      el.setAttribute('stroke', stroke)
    }

    if (!el.getAttribute('fill')) {
      // line/polyline are stroke-only primitives — do not paint a default fill
      if (el.tagName.toLowerCase() === 'line' || el.tagName.toLowerCase() === 'polyline') {
        if (!isReplaceableColor(el.getAttribute('stroke'))) {
          el.setAttribute('stroke', '#000000')
        }
        return
      }
      el.setAttribute('fill', '#000000')
      return
    }

    if (el.getAttribute('fill') === 'none' && !isReplaceableColor(el.getAttribute('stroke'))) {
      el.setAttribute('stroke', '#000000')
    }
  })

  // Groups often carry fill="none" + stroke for line-style Noun Project icons
  doc.querySelectorAll('svg, g').forEach((el: Element) => {
    const fill = normalizeNamedBlack(el.getAttribute('fill'))
    const stroke = normalizeNamedBlack(el.getAttribute('stroke'))

    if (fill && fill !== el.getAttribute('fill')) {
      el.setAttribute('fill', fill)
    }
    if (stroke && stroke !== el.getAttribute('stroke')) {
      el.setAttribute('stroke', stroke)
    }

    if (el.getAttribute('fill') === 'none' && !isReplaceableColor(el.getAttribute('stroke'))) {
      el.setAttribute('stroke', '#000000')
    }
  })

  // Normalize named blacks in inline styles and <style> blocks
  const replaceNamedBlackInCss = (css: string) =>
    css.replace(/((?:^|[{;\s])(?:fill|stroke)\s*:\s*)(?:black|currentColor)\b/gi, '$1#000000')

  doc.querySelectorAll('[style]').forEach((el: Element) => {
    const style = el.getAttribute('style')
    if (!style) return
    el.setAttribute('style', replaceNamedBlackInCss(style))
  })

  doc.querySelectorAll('style').forEach((styleEl) => {
    if (!styleEl.textContent) return
    styleEl.textContent = replaceNamedBlackInCss(styleEl.textContent)
  })

  return new XMLSerializer().serializeToString(doc)
}

export const removeDuplicateFill = (svgString: string): string => {
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml')
  const elements = Array.from(svgDoc.querySelectorAll('*'))

  elements.forEach((element) => {
    // Only inspect this element's opening tag. Matching fill= on outerHTML used to
    // treat nested children as "duplicate" fills and strip the child's real color.
    const outerHTML = element.outerHTML
    const openingTagMatch = outerHTML.match(/^<[^>]+>/)
    if (!openingTagMatch) return

    const openingTag = openingTagMatch[0]
    const fillAttrs = openingTag.match(/fill="[^"]*"/g)
    if (!fillAttrs || fillAttrs.length < 2) return

    let cleanedOpeningTag = openingTag
    for (let i = 1; i < fillAttrs.length; i++) {
      cleanedOpeningTag = cleanedOpeningTag.replace(fillAttrs[i], '')
    }
    element.outerHTML = outerHTML.replace(openingTag, cleanedOpeningTag)
  })

  return new XMLSerializer().serializeToString(svgDoc)
}

export const sanitizeAndUploadIcon = async (svgString: string, fileName: string): Promise<string> => {
  const sanitizedSvg = pipe(removeDuplicateFill, ensureBlackFill)(svgString)
  const blob = new Blob([sanitizedSvg], { type: 'image/svg+xml' })
  const s3Url = await handleUpload(new File([blob], fileName, { type: 'image/svg+xml' }))
  if (!s3Url) throw new Error('Failed to upload icon to S3')

  return s3Url
}
